import { z } from 'zod';

/**
 * Schema für einen einzelnen Footer-Link.
 */
export const FooterLinkSchema = z.object({
  /** Sichtbare Bezeichnung des Links */
  label: z.string().min(1),
  /** Ziel-URL des Links — erlaubt nur interne Pfade (/...), Anchors (#...) und https?://-URLs */
  url: z
    .string()
    .min(1)
    .refine((v) => v.startsWith('/') || v.startsWith('#') || /^https?:\/\//i.test(v), {
      message: 'URL muss ein interner Pfad (/), ein Anchor (#) oder eine https?://-URL sein',
    }),
});

/** TypeScript Type für einen Footer-Link */
export type FooterLink = z.infer<typeof FooterLinkSchema>;

/**
 * Schema für eine Footer-Sektion (Spalte).
 */
export const FooterSectionSchema = z.object({
  /** Überschrift der Sektion */
  title: z.string().min(1),
  /** Liste der Links innerhalb der Sektion */
  links: z.array(FooterLinkSchema),
});

/** TypeScript Type für eine Footer-Sektion */
export type FooterSection = z.infer<typeof FooterSectionSchema>;

/**
 * Schema für die gesamte Footer-Konfiguration.
 * Wird unter dem Setting-Key `footer.links` als JSON gespeichert.
 */
export const FooterConfigSchema = z.object({
  /** Liste aller Footer-Sektionen */
  sections: z.array(FooterSectionSchema),
});

/** TypeScript Type für die Footer-Konfiguration */
export type FooterConfig = z.infer<typeof FooterConfigSchema>;

/**
 * Standard-Footer-Konfiguration.
 * Wird als Fallback verwendet wenn kein DB-Eintrag vorhanden ist.
 */
export const DEFAULT_FOOTER_CONFIG: FooterConfig = {
  sections: [
    {
      title: 'Navigation',
      links: [
        { label: 'Startseite', url: '/' },
        { label: 'Tickets', url: '/tickets' },
        { label: 'ICON League', url: '/icon-league' },
        { label: 'Galerie', url: '/galerie' },
      ],
    },
    {
      title: 'Verein',
      links: [
        { label: 'Über uns', url: '/uber-uns' },
        { label: 'Vorstand', url: '/uber-uns#vorstand' },
        { label: 'Mitglied werden', url: '/uber-uns#mitglied' },
        { label: 'Kontakt', url: '/uber-uns#kontakt' },
      ],
    },
    {
      title: 'Rechtliches',
      links: [
        { label: 'Impressum', url: '/impressum' },
        { label: 'Datenschutz', url: '/datenschutz' },
        { label: 'Changelog', url: '/changelog' },
      ],
    },
  ],
};
